import { useState } from 'react';

import {
  Box,
  Button,
  FieldSet,
  FormField,
  Heading,
  Input,
  Label,
  LabeledControl,
  Legend,
  Modal,
  Paragraph,
  Radio,
  TextButton,
} from '@zillow/constellation-10';

import { OPT_OUT_REASON_OPTIONS } from './constants';

import type { OptOutReason } from './constants';

const DETAILS_MAX_LENGTH = 500;

export type OptOutSurveyAnswer = {
  reason: OptOutReason;
  details: string | undefined;
};

type Props = {
  onClose: () => void;
  onSubmit: (answer: OptOutSurveyAnswer) => void;
  isSubmitting?: boolean;
};

const ApplicationsOptOutSurveyModal = ({ onClose, onSubmit, isSubmitting = false }: Props) => {
  const [reason, setReason] = useState<OptOutReason>();
  const [details, setDetails] = useState('');

  const handleSubmit = () => {
    if (reason === undefined) {
      return;
    }

    onSubmit({ reason, details: details.trim() || undefined });
  };

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && !isSubmitting && onClose()}
      header={<Heading level={2}>Why are you turning off Zillow Applications?</Heading>}
      footer={
        <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <TextButton tone="brand" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </TextButton>
          <Button
            tone="brand"
            emphasis="filled"
            onClick={handleSubmit}
            disabled={reason === undefined}
            loading={isSubmitting}
          >
            Submit
          </Button>
        </Box>
      }
      body={
        <Box
          data-testid="ApplicationsOptOutSurveyModal"
          css={{ display: 'flex', flexDirection: 'column', gap: 'loose' }}
        >
          <Paragraph>Select your main reason. Your feedback will help us make improvements.</Paragraph>

          <FieldSet required legend={<Legend>Reason for turning off Zillow applications</Legend>}>
            {OPT_OUT_REASON_OPTIONS.map((option) => (
              <LabeledControl
                key={option.reason}
                label={<Label>{option.label}</Label>}
                control={
                  <Radio
                    name="ApplicationsOptOutSurveyModal-reason"
                    checked={option.reason === reason}
                    onChange={() => setReason(option.reason)}
                  />
                }
              />
            ))}
          </FieldSet>

          <FormField
            label={<Label optional>Tell us more</Label>}
            control={
              <Input
                value={details}
                maxLength={DETAILS_MAX_LENGTH}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDetails(e.target.value)}
              />
            }
          />
        </Box>
      }
    />
  );
};

export default ApplicationsOptOutSurveyModal;
